import { useState } from "react";
import { Button, Typography } from "antd";

import { PageTemplate } from "@/components/Templates/PageTemplate";
import ImagingStudyImageViewer from "@/components/ImagingFileViewer/ImagingStudyImageViewer";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";

// ECG is modelled on the same core tables (the exam + central-read shape) but
// is not radiology: the backend excludes modality "ECG" from this dataset
// server-side, and ECG lives on the dedicated ECG screen (with the waveform
// viewer). This screen is radiology-only.

export default function ImagingStudies() {
  // DICOM viewer: which study's imaging files are open (null = closed).
  const [viewerStudyId, setViewerStudyId] = useState<string | null>(null);

  // The DICOM viewer is demo-only until real imaging storage lands for Clario,
  // so the "View Images" action is gated on the demo-specific flag.
  const imagingViewerEnabled = useFeatureFlag(FEATURE_FLAGS.IMAGING_VIEWER);

  // "View Images" action column — only when the viewer flag is on, and only for
  // studies that actually have imaging series.
  const extraColumns = imagingViewerEnabled
    ? [
        {
          key: "view_images",
          title: "",
          width: 130,
          fixed: "right" as const,
          render: (_: unknown, row: Record<string, unknown>) => {
            const studyId = row.imaging_study_id as string | undefined;
            const seriesCount = Number(row.series_count ?? 0);
            if (!studyId || !seriesCount) {
              return (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  No images
                </Typography.Text>
              );
            }
            return (
              <Button size="small" onClick={() => setViewerStudyId(studyId)}>
                View Images
              </Button>
            );
          },
        },
      ]
    : [];

  return (
    <>
      <PageTemplate
        title="Measures: Imaging Studies"
        tooltipText="Explore imaging study metadata (modality, scanner, series and read counts) per subject per visit."
        type="imaging_studies"
        constants={{
          wikiLink: "/wiki/guides/exploration-measures-imagingstudies",
          idLink: undefined,
        }}
        rowKey="imaging_study_id"
        extraColumns={extraColumns}
      />
      <ImagingStudyImageViewer
        imagingStudyId={viewerStudyId}
        onClose={() => setViewerStudyId(null)}
      />
    </>
  );
}
